import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';

import {
	createIncomingLinksSsrState,
	publishIncomingLinksSsrState,
	resetIncomingLinksSsrState,
	type IncomingLinksSsrState,
} from './incomingLinksSsrState.ts';
import {
	preloadIncomingLinksSample,
	type IncomingLinksSampleResult,
} from './preloadIncomingLinksSample.ts';

type IncomingLinksSsrRoute = 'view';

type PreloadIncomingLinksSsrStateArgs = Readonly<{
	cloudId: string;
	contentId: string;
	route: IncomingLinksSsrRoute;
}>;

const INCOMING_LINKS_SAMPLE_OUTCOME_MEASURE = 'ssr-app/prepare/incomingLinksSample';

const recordIncomingLinksSampleOutcome = (
	result: IncomingLinksSampleResult,
	route: IncomingLinksSsrRoute,
): void => {
	let outcome: string;
	if (result.status === 'unavailable') {
		outcome = result.reason;
	} else if (result.sourceAris.length === 0) {
		outcome = 'empty';
	} else if (result.sourceAris.length >= 3) {
		outcome = 'limit-reached';
	} else {
		outcome = 'one-or-two';
	}

	SSRMeasures.addMeasure(`${INCOMING_LINKS_SAMPLE_OUTCOME_MEASURE}/${route}/outcome:${outcome}`, 0);
};

const createStateFromSample = (
	contentId: string,
	result: IncomingLinksSampleResult,
): IncomingLinksSsrState => {
	if (result.status === 'unavailable') {
		return createIncomingLinksSsrState(contentId, 'unavailable');
	}

	if (result.sourceAris.length > 0) {
		return createIncomingLinksSsrState(contentId, 'present');
	}

	return createIncomingLinksSsrState(contentId, 'empty');
};

export const preloadIncomingLinksSsrState = async ({
	cloudId,
	contentId,
	route,
}: PreloadIncomingLinksSsrStateArgs): Promise<IncomingLinksSsrState> => {
	// Clear any state left by an earlier preload before publishing this request's result.
	resetIncomingLinksSsrState();

	let samplePromise: Promise<IncomingLinksSampleResult> | undefined;
	const getSample = (): Promise<IncomingLinksSampleResult> => {
		samplePromise ??= preloadIncomingLinksSample({ cloudId, contentId });
		return samplePromise;
	};
	const completedWithinGuard = await slowQueryGuard(
		'preloadIncomingLinksSample',
		async () => {
			await getSample();
		},
		SLOW_QUERY_DEFAULT_TIMEOUT,
	);

	if (!completedWithinGuard) {
		SSRMeasures.addMeasure(`${INCOMING_LINKS_SAMPLE_OUTCOME_MEASURE}/${route}/outcome:timeout`, 0);
		return publishIncomingLinksSsrState(createIncomingLinksSsrState(contentId, 'unavailable'));
	}

	const sample = await getSample();
	recordIncomingLinksSampleOutcome(sample, route);
	return publishIncomingLinksSsrState(createStateFromSample(contentId, sample));
};
